// Delivery zones - SINGLE editable source for the three-zone delivery model.
//
// MIRRORED in server/src/utils/delivery.js (keep the two in sync). The server
// re-computes the fee authoritatively when creating the Stripe PaymentIntent,
// so this client copy only drives the live preview / postcode checker.
//
// To change coverage, edit the ranges below. Each entry is an inclusive
// [minPostcode, maxPostcode] pair.
//
// PRECEDENCE: Zone 2 is tested BEFORE Zone 1. Where the ranges overlap
// (3176-3200 sits numerically inside the 3000-3207 metro block, but is the
// outer south-east beyond Dandenong), the postcode resolves to Zone 2 ($150) -
// matching the intent that free delivery stops around Dandenong. Flip the order
// in `deliveryZone()` if that is ever not desired.

// Zone 1 - FREE delivery. Melbourne metro west, inner, and south-east to Dandenong.
export const ZONE1_RANGES = [
  [3000, 3207], // Melbourne metro core (CBD, inner, bayside)
  [3011, 3042], // inner + north-west
  [3335, 3338], // Melton / western growth corridor
  [3427, 3430], // Sunbury / north-west
  [3020, 3038], // western suburbs (incl. Hoppers Crossing 3029)
  [3145, 3170], // south-east to Clayton / Mount Waverley
  [3172, 3175], // Springvale South to Dandenong
];

// Zone 2 - $150 FLAT. Outer Melbourne and the near-regional edge, up to (not
// beyond) Pakenham and Torquay.
export const ZONE2_RANGES = [
  [3176, 3200], // outer south-east beyond Dandenong (Noble Park to Frankston)
  [3800, 3812], // Berwick / Pakenham corridor
  [3214, 3220], // Geelong
  [3228, 3231], // Torquay / surf-coast edge
];

export const ZONE2_FEE_CENTS = 15000; // $150 AUD
// Back-compat alias for older imports.
export const NON_METRO_FEE_CENTS = ZONE2_FEE_CENTS;

// The delivery promise in one sentence, and its short form for perk lists.
// Zone 3 is not only interstate - it includes parts of Victoria such as
// Cranbourne - so the wording is "elsewhere and interstate", never just
// "interstate".
export const DELIVERY_SUMMARY = `Free delivery to most of Melbourne, $${ZONE2_FEE_CENTS / 100} to outer suburbs, freight quoted elsewhere and interstate.`;
export const FREIGHT_SHORT = 'Freight quoted interstate';

function normalisePostcode(postcode) {
  if (postcode == null) return null;
  const s = String(postcode).trim();
  if (!/^\d{4}$/.test(s)) return null;
  return parseInt(s, 10);
}

const inRanges = (n, ranges) => ranges.some(([lo, hi]) => n >= lo && n <= hi);

// 1 (free) | 2 ($150) | 3 (freight quoted separately) | null (invalid postcode)
export function deliveryZone(postcode) {
  const n = normalisePostcode(postcode);
  if (n == null) return null;
  if (inRanges(n, ZONE2_RANGES)) return 2; // Zone 2 wins on overlap - see header note
  if (inRanges(n, ZONE1_RANGES)) return 1;
  return 3;
}

// 0 (free) | 15000 ($150) | null (Zone 3 - no fixed fee; freight quoted separately)
export function calculateDeliveryFeeCents(postcode) {
  const z = deliveryZone(postcode);
  if (z === 1) return 0;
  if (z === 2) return ZONE2_FEE_CENTS;
  return null;
}

// Back-compat: "free delivery" means Zone 1.
export function isMelbMetro(postcode) {
  return deliveryZone(postcode) === 1;
}

// Result object for the storefront postcode checker.
export function deliveryCheck(postcode) {
  const zone = deliveryZone(postcode);
  if (zone === 1) return { zone, feeCents: 0, message: 'Free delivery to your area' };
  if (zone === 2) return { zone, feeCents: ZONE2_FEE_CENTS, message: '$150 delivery to your area' };
  if (zone === 3) return { zone, feeCents: null, message: 'We ship Australia wide - delivery quoted separately for your postcode, we will confirm before charging you.' };
  return { zone: null, feeCents: null, message: '' };
}
